import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Database, HardDrive, RefreshCw, FileText, Archive, AlertTriangle, Layers, Hash } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DatabaseMonitor = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setRefreshing(true);
      const response = await axios.get(`${API}/database/stats`);
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching database stats:", error);
      toast.error("Failed to load database statistics");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Assume 512MB limit for display purposes (adjust based on actual limits)
  const STORAGE_LIMIT_MB = 512;
  const usagePercentage = stats ? Math.min((stats.storage_size_mb / STORAGE_LIMIT_MB) * 100, 100) : 0;

  const getUsageColor = (percentage) => {
    if (percentage < 50) return "bg-green-500";
    if (percentage < 75) return "bg-yellow-500";
    return "bg-red-500";
  };

  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(3)} MB`;
  };

  if (loading) {
    return (
      <Card className="bg-black/40 border-cyan-500/30">
        <CardContent className="p-6">
          <div className="flex items-center justify-center gap-2 text-gray-400">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Loading database statistics...
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-cyan-400 flex items-center gap-2">
          <Database className="h-6 w-6" />
          Database Monitor
        </h2>
        <Button
          onClick={fetchStats}
          disabled={refreshing}
          variant="outline"
          className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/20"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Overview Cards - 2 rows */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-black/40 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <HardDrive className="h-4 w-4" />
              Total Data Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {stats?.total_size_mb?.toFixed(3) || 0} <span className="text-sm text-gray-400">MB</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <Archive className="h-4 w-4" />
              Storage Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {stats?.storage_size_mb?.toFixed(3) || 0} <span className="text-sm text-gray-400">MB</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Index Size
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-white">
              {stats?.index_size_mb?.toFixed(3) || 0} <span className="text-sm text-gray-400">MB</span>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-cyan-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <Layers className="h-4 w-4" />
              Collections
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-cyan-400">
              {stats?.collections_count || 0}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-green-500/30 col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <Hash className="h-4 w-4" />
              Total Documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-400">
              {stats?.total_documents?.toLocaleString() || 0}
            </div>
            <p className="text-xs text-gray-500 mt-1">Across all {stats?.collections_count || 0} collections</p>
          </CardContent>
        </Card>

        <Card className="bg-black/40 border-purple-500/30 col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-gray-400 flex items-center gap-2">
              <Database className="h-4 w-4" />
              Database
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-xl font-bold text-purple-400">
              {stats?.database_name || 'N/A'}
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Calculated: {stats?.calculated_storage_size_mb?.toFixed(3) || 0} MB storage
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Storage Usage Bar */}
      <Card className="bg-black/40 border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-cyan-400" />
              Storage Usage
            </span>
            <span className={`text-sm px-2 py-1 rounded ${
              usagePercentage < 50 ? 'bg-green-500/20 text-green-400' :
              usagePercentage < 75 ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-red-500/20 text-red-400'
            }`}>
              {usagePercentage.toFixed(1)}% Used
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="h-4 bg-gray-700 rounded-full overflow-hidden">
              <div 
                className={`h-full ${getUsageColor(usagePercentage)} transition-all duration-500`}
                style={{ width: `${usagePercentage}%` }}
              />
            </div>
            <div className="flex justify-between text-sm text-gray-400">
              <span>{stats?.total_size_mb?.toFixed(2) || 0} MB used</span>
              <span>{STORAGE_LIMIT_MB} MB estimated limit</span>
            </div>
            {usagePercentage > 75 && (
              <div className="flex items-center gap-2 mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                <span className="text-red-400 text-sm">
                  Storage usage is high. Consider archiving old data to free up space.
                </span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Collection Details */}
      <Card className="bg-black/40 border-cyan-500/30">
        <CardHeader>
          <CardTitle className="text-lg text-white flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Database className="h-5 w-5 text-cyan-400" />
              Collection Details
            </span>
            <span className="text-sm text-gray-400 font-normal">
              Sorted by storage size (largest first)
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">#</th>
                  <th className="text-left py-3 px-3 text-gray-400 font-medium">Collection</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Documents</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Data Size</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Avg Doc</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Storage</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Indexes</th>
                  <th className="text-right py-3 px-3 text-gray-400 font-medium">Index Size</th>
                </tr>
              </thead>
              <tbody>
                {stats?.collections?.map((coll, idx) => (
                  <tr key={idx} className={`border-b border-gray-700/50 hover:bg-gray-800/30 ${coll.error ? 'opacity-50' : ''}`}>
                    <td className="py-2 px-3 text-gray-500">{idx + 1}</td>
                    <td className="py-2 px-3">
                      <span className="text-white font-medium">{coll.name}</span>
                      {coll.error && <span className="text-red-400 text-xs ml-2">(error)</span>}
                    </td>
                    <td className="text-right py-2 px-3 text-cyan-300 font-mono">
                      {coll.document_count?.toLocaleString() || 0}
                    </td>
                    <td className="text-right py-2 px-3 text-white font-mono">
                      {coll.size_mb?.toFixed(4) || '0.0000'}
                    </td>
                    <td className="text-right py-2 px-3 text-gray-400 font-mono">
                      {coll.avg_doc_size_bytes ? formatBytes(coll.avg_doc_size_bytes) : '-'}
                    </td>
                    <td className="text-right py-2 px-3 text-green-400 font-mono">
                      {coll.storage_size_mb?.toFixed(4) || '0.0000'}
                    </td>
                    <td className="text-right py-2 px-3 text-purple-400 font-mono">
                      {coll.index_count || '-'}
                    </td>
                    <td className="text-right py-2 px-3 text-yellow-400 font-mono">
                      {coll.index_size_mb?.toFixed(4) || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-600 bg-gray-900/50">
                  <td className="py-3 px-3" colSpan={2}>
                    <span className="text-cyan-400 font-bold">TOTAL ({stats?.collections?.length || 0} collections)</span>
                  </td>
                  <td className="text-right py-3 px-3 text-cyan-300 font-mono font-bold">
                    {stats?.total_documents?.toLocaleString() || 0}
                  </td>
                  <td className="text-right py-3 px-3 text-white font-mono font-bold">
                    {stats?.calculated_data_size_mb?.toFixed(4) || '0.0000'}
                  </td>
                  <td className="text-right py-3 px-3 text-gray-400">-</td>
                  <td className="text-right py-3 px-3 text-green-400 font-mono font-bold">
                    {stats?.calculated_storage_size_mb?.toFixed(4) || '0.0000'}
                  </td>
                  <td className="text-right py-3 px-3 text-gray-400">-</td>
                  <td className="text-right py-3 px-3 text-yellow-400 font-mono font-bold">
                    {stats?.index_size_mb?.toFixed(4) || '0.0000'}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Last Updated */}
      <div className="text-center text-sm text-gray-500">
        Last updated: {stats?.timestamp ? new Date(stats.timestamp).toLocaleString() : '-'}
      </div>
    </div>
  );
};

export default DatabaseMonitor;
